import { type DatabaseLayoutType } from '@databases/types/database/view';

import { type FieldConfiguration } from './configuration-parsers/parse-configuration';
import {
  type AddEntryToken,
  type AddHiddenFieldToken,
  type DatabaseSortDirection,
  type EditEntryToken,
  type EditFieldSettingToken,
  type EditSortToken,
  type EditViewSettingToken,
  type FieldToken,
  type LayoutToken,
  type RemoveEntryToken,
  type RemoveFieldToken,
  type RemoveFilterToken,
  type RemoveHiddenFieldToken,
  type RemoveSortToken,
  type RemoveViewToken,
  type SortToken,
  TokenType,
  type ValueToken,
  type ViewToken,
} from './types';

export const value = (fieldName: string, val: any = null): ValueToken => ({
  tokenType: TokenType.VALUE,
  fieldName,
  value: val,
  resetValue: false,
});

export const valueWithReset = (fieldName: string, val: any = null): ValueToken => ({
  tokenType: TokenType.VALUE,
  fieldName,
  value: val,
  resetValue: true,
});

export const field = (name: string, type: string): FieldToken => ({
  tokenType: TokenType.FIELD,
  name,
  type,
});

export const view = (name: string): ViewToken => ({
  tokenType: TokenType.VIEW,
  name,
});

export const layout = (
  type: 'board' | 'table' | 'gallery' | 'calendar',
  viewName: string,
  groupField?: string
): LayoutToken => ({
  tokenType: TokenType.LAYOUT,
  type: type as DatabaseLayoutType,
  viewName,
  groupField,
});

export const sort = (
  fieldName: string,
  viewName: string,
  direction?: DatabaseSortDirection
): SortToken => ({
  tokenType: TokenType.SORT,
  fieldName,
  viewName,
  direction,
});

export const addEntry = (): AddEntryToken => ({
  tokenType: TokenType.ADD_ENTRY,
});

export const editEntry = (rowNumber: string): EditEntryToken => ({
  tokenType: TokenType.EDIT_ENTRY,
  rowNumber,
});

export const removeEntry = (rowNumber: string): RemoveEntryToken => ({
  tokenType: TokenType.REMOVE_ENTRY,
  rowNumber,
});

export const removeField = (fieldName: string): RemoveFieldToken => ({
  tokenType: TokenType.REMOVE_FIELD,
  fieldName,
});

export const editSort = (
  viewName: string,
  fieldName: string,
  direction: DatabaseSortDirection
): EditSortToken => ({
  tokenType: TokenType.EDIT_SORT,
  viewName,
  fieldName,
  direction,
});

export const removeSort = (
  viewName: string,
  fieldName: string,
  direction?: DatabaseSortDirection
): RemoveSortToken => ({
  tokenType: TokenType.REMOVE_SORT,
  viewName,
  fieldName,
  direction,
});

export const removeView = (viewName: string): RemoveViewToken => ({
  tokenType: TokenType.REMOVE_VIEW,
  viewName,
});

export const removeFilter = (viewName: string): RemoveFilterToken => ({
  tokenType: TokenType.REMOVE_FILTER,
  viewName,
});

export const editViewSetting = (
  viewName: string,
  property: string,
  value: string
): EditViewSettingToken => ({
  tokenType: TokenType.EDIT_VIEW_SETTING,
  viewName,
  property,
  value,
});

export const editFieldSetting = (
  fieldName: string,
  property: string,
  value: string | boolean | FieldConfiguration
): EditFieldSettingToken => ({
  tokenType: TokenType.EDIT_FIELD_SETTING,
  fieldName,
  property,
  value,
});

export const addHiddenField = (
  viewName: string,
  fieldNames: string[]
): AddHiddenFieldToken => ({
  tokenType: TokenType.ADD_HIDDEN_FIELD,
  viewName,
  fieldNames,
});

export const removeHiddenField = (
  viewName: string,
  fieldNames: string[]
): RemoveHiddenFieldToken => ({
  tokenType: TokenType.REMOVE_HIDDEN_FIELD,
  viewName,
  fieldNames,
});
